import React, { useState } from 'react';
import { Upload, FileText, Copy, ArrowLeft, AlertTriangle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const VanGoTwoMinutesReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations - Accept Excel files
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.xlsx', '.xls'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) && !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
        setError('Please upload an Excel file only (.xlsx or .xls)');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('excelFile', file);

      // Send file to server for processing
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/vangotwominutes`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTendencyFlag = (percentage) => {
    const pct = parseFloat(percentage);
    if (pct >= 80) return ' ❗ EXTREMELY VALUABLE';
    if (pct >= 70) return ' ⚠️ VALUABLE';
    return '';
  };

  const generateReportText = () => {
    if (!report) return '';

    let text = `⏰ VANGO 2-MINUTE ANALYSIS REPORT\n`;
    text += `Generated on ${new Date().toLocaleDateString()}\n\n`;

    if (report.logos?.clientLogo || report.logos?.companyLogo) {
      text += `📋 REPORT DETAILS\n`;
      if (report.logos?.clientLogo) text += `Client: [Logo included]\n`;
      if (report.logos?.companyLogo) text += `Analysis by: [Company logo included]\n`;
      text += `\n`;
    }

    text += `📊 FOUNDATION DATA\n`;
    text += `Using file: ${file?.name}\n`;
    text += `Total Offensive Plays: ${report.foundation?.totalPlays || 0}\n`;
    text += `Run: ${report.foundation?.runPlays || 0} plays (${report.foundation?.runPercent || 0}%)\n`;
    text += `Pass: ${report.foundation?.passPlays || 0} plays (${report.foundation?.passPercent || 0}%)\n`;
    text += `First Half 2-Minute (Q2): ${report.foundation?.q2PlaysCount || 0} plays\n`;
    text += `Game 2-Minute (Q4): ${report.foundation?.q4PlaysCount || 0} plays\n\n`;

    if (report.q2Personnel) {
      text += `🏈 FIRST HALF 2-MINUTE (Q2) PERSONNEL BREAKDOWN\n`;
      if (report.q2Personnel.length > 0) {
        report.q2Personnel.forEach((group) => {
          text += `${group.personnel} Personnel: ${group.total} plays (${group.runs} Runs / ${group.passes} Passes) - `;
          text += `${group.runPercent}% Run${getTendencyFlag(group.runPercent)} / ${group.passPercent}% Pass${getTendencyFlag(group.passPercent)}\n`;
        });
      } else {
        text += `No Q2 plays found.\n`;
      }
      text += `\n`;
    }

    if (report.q2Passing) {
      text += `🎯 Q2 PASSING ANALYSIS\n`;
      text += `Overall Passing: ${report.q2Passing.attempts} Attempts | ${report.q2Passing.completions} Completions | ${report.q2Passing.completionRate}% Completion Rate\n`;
      if (report.q2Passing.untargetedAttempts > 0) {
        text += `Attempt Breakdown: ${report.q2Passing.targetedAttempts} targeted attempts (${report.q2Passing.targetedCompletions} completions, ${report.q2Passing.targetedCompletionRate}% completion rate), ${report.q2Passing.untargetedAttempts} untargeted attempts\n`;
      }
      if (report.q2Passing.receivers?.length > 0) {
        text += `Receiver Targets:\n`;
        report.q2Passing.receivers.forEach((receiver) => {
          text += `  • ${receiver.receiver}: ${receiver.targets} targets, ${receiver.completions} completions (${receiver.completionRate}%), ${receiver.deepBalls} deep balls\n`;
        });
      }
      text += `\n`;
    }

    if (report.q4Personnel) {
      text += `🏈 GAME 2-MINUTE (Q4) PERSONNEL BREAKDOWN\n`;
      if (report.q4Personnel.length > 0) {
        report.q4Personnel.forEach((group) => {
          text += `${group.personnel} Personnel: ${group.total} plays (${group.runs} Runs / ${group.passes} Passes) - `;
          text += `${group.runPercent}% Run${getTendencyFlag(group.runPercent)} / ${group.passPercent}% Pass${getTendencyFlag(group.passPercent)}\n`;
        });
      } else {
        text += `No Q4 plays found.\n`;
      }
      text += `\n`;
    }

    if (report.q4Passing) {
      text += `🎯 Q4 PASSING ANALYSIS\n`;
      text += `Overall Passing: ${report.q4Passing.attempts} Attempts | ${report.q4Passing.completions} Completions | ${report.q4Passing.completionRate}% Completion Rate\n`;
      if (report.q4Passing.untargetedAttempts > 0) {
        text += `Attempt Breakdown: ${report.q4Passing.targetedAttempts} targeted attempts (${report.q4Passing.targetedCompletions} completions, ${report.q4Passing.targetedCompletionRate}% completion rate), ${report.q4Passing.untargetedAttempts} untargeted attempts\n`;
      }
      if (report.q4Passing.receivers?.length > 0) {
        text += `Receiver Targets:\n`;
        report.q4Passing.receivers.forEach((receiver) => {
          text += `  • ${receiver.receiver}: ${receiver.targets} targets, ${receiver.completions} completions (${receiver.completionRate}%), ${receiver.deepBalls} deep balls\n`;
        });
      }
      text += `\n`;
    }

    if (report.criticalTendencies) {
      text += `🚨 CRITICAL TENDENCIES (≥70%)\n`;
      
      const q2Tendencies = report.criticalTendencies.filter(t => t.quarter === 'Q2');
      const q4Tendencies = report.criticalTendencies.filter(t => t.quarter === 'Q4');
      
      if (q2Tendencies.length > 0) {
        text += `First Half 2-Minute (Q2):\n`;
        q2Tendencies.forEach(tendency => {
          text += `  • ${tendency.personnel} Personnel = ${tendency.tendency} (${tendency.type} - ${tendency.sample})\n`;
        });
        text += `\n`;
      }

      if (q4Tendencies.length > 0) {
        text += `Game 2-Minute (Q4):\n`;
        q4Tendencies.forEach(tendency => {
          text += `  • ${tendency.personnel} Personnel = ${tendency.tendency} (${tendency.type} - ${tendency.sample})\n`;
        });
      }

      if (report.criticalTendencies.length === 0) {
        text += `No critical tendencies identified (≥70%).\n`;
      }
    }

    return text;
  };

  const copyReport = async () => {
    if (!report) return;

    const reportText = generateReportText();
    
    try {
      await navigator.clipboard.writeText(reportText);
      showSuccessMessage('Report copied to clipboard!');
    } catch (err) {
      setError('Failed to copy to clipboard. Please try again.');
    }
  };

  const showSuccessMessage = (message) => {
    const originalError = error;
    setError(null);
    
    const alertDiv = document.createElement('div');
    alertDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;
    alertDiv.textContent = message;
    document.body.appendChild(alertDiv);
    
    setTimeout(() => {
      document.body.removeChild(alertDiv);
      setError(originalError);
    }, 3000);
  };

  const renderReport = () => {
    if (!report) return null;

    return (
      <Box sx={{ mt: 2, bgcolor: 'white', minHeight: '100vh' }}>
        {/* Header Section - Using Shared Component */}
        <ReportHeader 
          title="VANGO 2-MINUTE ANALYSIS"
          logos={report.logos}
          fileName={file?.name}
        />

        {/* Report Content */}
        <Box sx={{ p: 3 }}>

        {/* Foundation Data Section */}
        <Box sx={{ mb: 4 }} data-section="vango-two-minutes-foundation-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            FOUNDATION DATA
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#f8f9fa'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.totalPlays || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  TOTAL PLAYS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#e8f5e8'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.runPlays || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  RUNS ({report.foundation?.runPercent || 0}%)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#e3f2fd'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.passPlays || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  PASSES ({report.foundation?.passPercent || 0}%)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#fff3e0'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.q2PlaysCount || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  Q2 PLAYS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#f3e5f5'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.q4PlaysCount || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  Q4 PLAYS
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Q2 Personnel Analysis */}
        <Box sx={{ mb: 4 }} data-section="vango-two-minutes-q2-personnel-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            FIRST HALF 2-MINUTE (Q2) PERSONNEL BREAKDOWN
          </Typography>
          
          {report.q2Personnel?.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '2px solid #000' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e9ecef' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PERSONNEL</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PLAYS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>RUNS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PASSES</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>RUN %</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PASS %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.q2Personnel.map((group, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.personnel}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.total}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.runs}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.passes}
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontSize: '0.9rem', 
                        border: '1px solid #000',
                        fontWeight: parseFloat(group.runPercent) >= 70 ? 'bold' : 'normal',
                        bgcolor: parseFloat(group.runPercent) >= 80 ? '#ffcdd2' : 
                                 parseFloat(group.runPercent) >= 70 ? '#fff3e0' : 'inherit'
                      }}>
                        {group.runPercent}%
                        {parseFloat(group.runPercent) >= 80 && ' 🚨'}
                        {parseFloat(group.runPercent) >= 70 && parseFloat(group.runPercent) < 80 && ' ⚠️'}
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontSize: '0.9rem', 
                        border: '1px solid #000',
                        fontWeight: parseFloat(group.passPercent) >= 70 ? 'bold' : 'normal',
                        bgcolor: parseFloat(group.passPercent) >= 80 ? '#e1f5fe' : 
                                 parseFloat(group.passPercent) >= 70 ? '#f3e5f5' : 'inherit'
                      }}>
                        {group.passPercent}%
                        {parseFloat(group.passPercent) >= 80 && ' 🚨'}
                        {parseFloat(group.passPercent) >= 70 && parseFloat(group.passPercent) < 80 && ' ⚠️'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" color="text.secondary">No Q2 plays found.</Typography>
          )}
        </Box>

        {/* Q2 Passing Analysis */}
        {report.q2Passing && (
          <Box sx={{ mb: 4 }} data-section="vango-two-minutes-q2-passing-section">
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 2 }}>
              Q2 PASSING ANALYSIS
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #000', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Overall Passing: {report.q2Passing.attempts} Attempts | {report.q2Passing.completions} Completions | {report.q2Passing.completionRate}% Completion Rate
              </Typography>
              
              {report.q2Passing.untargetedAttempts > 0 && (
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Attempt Breakdown: {report.q2Passing.targetedAttempts} targeted attempts ({report.q2Passing.targetedCompletions} completions, {report.q2Passing.targetedCompletionRate}% completion rate), {report.q2Passing.untargetedAttempts} untargeted attempts
                </Typography>
              )}

              {report.q2Passing.receivers?.length > 0 && (
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Receiver Targets:
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {report.q2Passing.receivers.map((receiver, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {receiver.receiver}: {receiver.targets} targets, {receiver.completions} completions ({receiver.completionRate}%), {receiver.deepBalls} deep balls
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Q4 Personnel Analysis */}
        <Box sx={{ mb: 4 }} data-section="vango-two-minutes-q4-personnel-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            GAME 2-MINUTE (Q4) PERSONNEL BREAKDOWN
          </Typography>
          
          {report.q4Personnel?.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '2px solid #000' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e9ecef' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PERSONNEL</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PLAYS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>RUNS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PASSES</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>RUN %</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>PASS %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.q4Personnel.map((group, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.personnel}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.total}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.runs}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {group.passes}
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontSize: '0.9rem', 
                        border: '1px solid #000',
                        fontWeight: parseFloat(group.runPercent) >= 70 ? 'bold' : 'normal',
                        bgcolor: parseFloat(group.runPercent) >= 80 ? '#ffcdd2' : 
                                 parseFloat(group.runPercent) >= 70 ? '#fff3e0' : 'inherit'
                      }}>
                        {group.runPercent}%
                        {parseFloat(group.runPercent) >= 80 && ' 🚨'}
                        {parseFloat(group.runPercent) >= 70 && parseFloat(group.runPercent) < 80 && ' ⚠️'}
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontSize: '0.9rem', 
                        border: '1px solid #000',
                        fontWeight: parseFloat(group.passPercent) >= 70 ? 'bold' : 'normal',
                        bgcolor: parseFloat(group.passPercent) >= 80 ? '#e1f5fe' : 
                                 parseFloat(group.passPercent) >= 70 ? '#f3e5f5' : 'inherit'
                      }}>
                        {group.passPercent}%
                        {parseFloat(group.passPercent) >= 80 && ' 🚨'}
                        {parseFloat(group.passPercent) >= 70 && parseFloat(group.passPercent) < 80 && ' ⚠️'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" color="text.secondary">No Q4 plays found.</Typography>
          )}
        </Box>

        {/* Q4 Passing Analysis */}
        {report.q4Passing && (
          <Box sx={{ mb: 4 }} data-section="vango-two-minutes-q4-passing-section">
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 2 }}>
              Q4 PASSING ANALYSIS
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #000', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Overall Passing: {report.q4Passing.attempts} Attempts | {report.q4Passing.completions} Completions | {report.q4Passing.completionRate}% Completion Rate
              </Typography>
              
              {report.q4Passing.untargetedAttempts > 0 && (
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Attempt Breakdown: {report.q4Passing.targetedAttempts} targeted attempts ({report.q4Passing.targetedCompletions} completions, {report.q4Passing.targetedCompletionRate}% completion rate), {report.q4Passing.untargetedAttempts} untargeted attempts
                </Typography>
              )}

              {report.q4Passing.receivers?.length > 0 && (
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Receiver Targets:
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {report.q4Passing.receivers.map((receiver, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {receiver.receiver}: {receiver.targets} targets, {receiver.completions} completions ({receiver.completionRate}%), {receiver.deepBalls} deep balls
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Critical Tendencies */}
        {report.criticalTendencies?.length > 0 && (
          <Box sx={{ mb: 4 }} data-section="vango-two-minutes-tendencies-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              CRITICAL TENDENCIES (≥70%)
            </Typography>
            
            {report.criticalTendencies.filter(t => t.quarter === 'Q2').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2, bgcolor: '#ffebee', p: 1, border: '1px solid #d32f2f' }}>
                  FIRST HALF 2-MINUTE (Q2):
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {report.criticalTendencies.filter(t => t.quarter === 'Q2').map((tendency, index) => (
                    <Box key={index} sx={{ 
                      p: 1, 
                      mb: 1, 
                      bgcolor: '#ffcdd2', 
                      border: '1px solid #d32f2f',
                      borderRadius: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {tendency.personnel} Personnel = {tendency.tendency} ({tendency.type} - {tendency.sample})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {report.criticalTendencies.filter(t => t.quarter === 'Q4').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 2, bgcolor: '#fff3e0', p: 1, border: '1px solid #f57c00' }}>
                  GAME 2-MINUTE (Q4):
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {report.criticalTendencies.filter(t => t.quarter === 'Q4').map((tendency, index) => (
                    <Box key={index} sx={{ 
                      p: 1, 
                      mb: 1, 
                      bgcolor: '#ffe0b2', 
                      border: '1px solid #f57c00',
                      borderRadius: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {tendency.personnel} Personnel = {tendency.tendency} ({tendency.type} - {tendency.sample})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {(!report.criticalTendencies || report.criticalTendencies.length === 0) && (
          <Box sx={{ mb: 4 }} data-section="vango-two-minutes-tendencies-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              CRITICAL TENDENCIES (≥70%)
            </Typography>
            <Typography variant="body1" color="text.secondary">
              No critical tendencies identified (≥70%).
            </Typography>
          </Box>
        )}
        
        </Box> {/* End Report Content */}
      </Box>
    );
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.VANGO_TWO_MINUTES,
      'vango-two-minutes-report.pdf',
      setError
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/reports"
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
        >
          Back to Reports
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Clock size={32} />
        VanGo 2-Minute Analysis Report
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Upload your Excel file containing 2-minute drill data to generate comprehensive analysis of personnel usage, 
        passing targets, and critical tendencies in critical game situations (Q2 and Q4).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Upload size={20} />
            Upload Excel File
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select an Excel file (.xlsx or .xls) containing 2-minute drill data with columns: QTR, R/P, PER, PaRes, pff_PASSWRTAR, pff_DEEPPASS. 
            The analysis will filter for Q2 and Q4 plays and exclude plays that didn't start (R/P = 'X').
          </Typography>

          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="excel-file-upload"
            disabled={loading}
          />
          <label htmlFor="excel-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Choose Excel File
            </Button>
          </label>

          {file && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected file:</strong> {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={processData}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FileText />}
          size="large"
        >
          {loading ? 'Generating Report...' : 'Generate 2-Minute Report'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={clearAll}
          disabled={loading}
        >
          Clear All
        </Button>
      </Box>

      {loading && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Processing your Excel data...</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The server is analyzing your Excel data and generating the 2-minute drill report. This may take a few moments.
            </Typography>
          </CardContent>
        </Card>
      )}

      {report && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Generated Report</Typography>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={copyReport}
              startIcon={<Copy />}
            >
              Copy Report
            </Button>
            <Button
              variant="outlined"
              onClick={saveReportAsPDF}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="styled-report-content">
            {renderReport()}
          </Box>
        </Box>
      )}

      {/* Instructions */}
      <Card sx={{ mt: 4, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AlertTriangle size={20} />
            2-Minute Drill Analysis Methodology
          </Typography>
          <Typography variant="body2" component="div">
            <strong>What is 2-Minute Drill Analysis?</strong>
            <p>Analysis of offensive tendencies in critical 2-minute situations at the end of each half (Q2) and game (Q4).</p>
            
            <strong>Required Excel Columns:</strong>
            <ul>
              <li><strong>QTR:</strong> Quarter number (2=Second Quarter, 4=Fourth Quarter)</li>
              <li><strong>R/P:</strong> Run/Pass indicator (R=run, P=pass, X=excluded/no snap)</li>
              <li><strong>PER:</strong> Personnel groupings (11, 12, 21, etc.)</li>
              <li><strong>PaRes:</strong> Pass Result (COM/Complete, INC/Incomplete, etc.)</li>
              <li><strong>pff_PASSWRTAR:</strong> Pass target receiver name</li>
              <li><strong>pff_DEEPPASS:</strong> Deep ball indicator (1, Y, or blank)</li>
            </ul>
            
            <strong>Analysis Process:</strong>
            <ol>
              <li>Filter for Q2 and Q4 plays only (2-minute situations)</li>
              <li>Exclude plays that didn't start (R/P = 'X')</li>
              <li>Analyze personnel usage and run/pass tendencies by quarter</li>
              <li>Track passing targets, completion rates, and deep ball usage</li>
              <li>Flag critical tendencies (⚠️ = 70%+, ❗ = 80%+)</li>
              <li>Provide separate analysis for Q2 vs Q4 situations</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VanGoTwoMinutesReportGenerator;